/**
 * Composant d'indicateur de santé Firestore
 * Affiche le statut de connexion et permet la reconnexion manuelle
 */

import React, { useState, useEffect } from 'react';
import { AlertTriangle, Wifi, WifiOff, RefreshCw } from 'lucide-react';
import firestoreHealthService, { FirestoreHealthStatus } from '../../services/firestoreHealthService';
import { toast } from 'react-toastify';

interface FirestoreHealthIndicatorProps {
  showDetails?: boolean;
  className?: string;
}

const FirestoreHealthIndicator: React.FC<FirestoreHealthIndicatorProps> = ({ 
  showDetails = false, 
  className = '' 
}) => {
  const [healthStatus, setHealthStatus] = useState<FirestoreHealthStatus>({
    isConnected: false,
    retryCount: 0
  });
  const [isReconnecting, setIsReconnecting] = useState(false);

  useEffect(() => {
    // Obtenir le statut initial
    setHealthStatus(firestoreHealthService.getHealthStatus());

    // Écouter les changements de statut
    const handleStatusChange = (status: FirestoreHealthStatus) => {
      setHealthStatus(status);
      
      // Afficher des notifications selon le statut
      if (status.isConnected && status.retryCount > 0) {
        toast.success('Connexion Firestore rétablie');
      } else if (!status.isConnected && status.lastError) {
        if (status.retryCount === 1) {
          toast.warning('Problème de connexion Firestore détecté');
        }
      }
    };

    firestoreHealthService.addStatusListener(handleStatusChange);

    return () => {
      firestoreHealthService.removeStatusListener(handleStatusChange);
    };
  }, []);

  const handleForceReconnect = async () => {
    setIsReconnecting(true);
    try {
      const success = await firestoreHealthService.forceReconnect();
      if (success) {
        toast.success('Reconnexion réussie');
      } else {
        toast.error('Échec de la reconnexion');
      }
    } catch (error) {
      toast.error('Erreur lors de la reconnexion');
    } finally {
      setIsReconnecting(false);
    }
  };

  const getStatusColor = () => {
    if (healthStatus.isConnected) return 'text-green-500';
    if (healthStatus.retryCount > 0) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getStatusIcon = () => {
    if (healthStatus.isConnected) {
      return <Wifi className="w-4 h-4" />;
    }
    if (healthStatus.retryCount > 0) {
      return <AlertTriangle className="w-4 h-4" />;
    }
    return <WifiOff className="w-4 h-4" />;
  };

  const getStatusText = () => {
    if (healthStatus.isConnected) {
      return 'Connecté';
    }
    if (healthStatus.retryCount > 0) {
      return `Reconnexion... (${healthStatus.retryCount})`;
    }
    return 'Déconnecté';
  };

  if (!showDetails && healthStatus.isConnected) {
    return null; // Ne rien afficher si tout va bien et mode simple
  }

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <div className={`flex items-center space-x-1 ${getStatusColor()}`}>
        {getStatusIcon()}
        <span className="text-sm font-medium">{getStatusText()}</span>
      </div>
      
      {!healthStatus.isConnected && (
        <button
          onClick={handleForceReconnect}
          disabled={isReconnecting}
          className="flex items-center space-x-1 px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Forcer la reconnexion"
        >
          <RefreshCw className={`w-3 h-3 ${isReconnecting ? 'animate-spin' : ''}`} />
          <span>{isReconnecting ? 'Reconnexion...' : 'Reconnecter'}</span>
        </button>
      )}
      
      {showDetails && (
        <div className="text-xs text-gray-500">
          {healthStatus.lastSuccessfulConnection && (
            <div>Dernière connexion: {healthStatus.lastSuccessfulConnection.toLocaleTimeString()}</div>
          )}
          {healthStatus.lastError && (
            <div className="text-red-500">Erreur: {healthStatus.lastError}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default FirestoreHealthIndicator;